import React from "react";
import { Show, useRecordContext } from "react-admin";
import { Box, Card, Typography, Grid, Chip, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
	Person as PersonIcon,
	Computer as ComputerIcon,
	Security as SecurityIcon,
	ArrowBack as ArrowBackIcon,
} from "@material-ui/icons";
import { formatDateTimeString } from "components/_helpers/dateTime";
import { ReferenceField, FunctionField } from "react-admin";

const useStyles = makeStyles((theme) => ({
	container: {
		backgroundColor: "#f5f5f5",
		minHeight: "100vh",
		padding: theme.spacing(2),
	},
	mainCard: {
		maxWidth: 1000,
		margin: "0 auto",
		backgroundColor: "white",
		border: "1px solid #e0e0e0",
	},
	header: {
		backgroundColor: "#2196f3",
		display: "flex",
		alignItems: "center",
		color: "white",
		padding: theme.spacing(2),
		borderBottom: "1px solid #1976d2",
	},
	backButton: {
		color: "white",
		marginRight: theme.spacing(1),
	},
	title: {
		fontSize: "1.5rem",
		fontWeight: 500,
		display: "inline",
	},
	content: {
		padding: theme.spacing(3),
	},
	section: {
		marginBottom: theme.spacing(3),
	},
	sectionTitle: {
		color: "#333",
		fontWeight: 600,
		marginBottom: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		fontSize: "1.1rem",
	},
	sectionIcon: {
		marginRight: theme.spacing(1),
		color: "#2196f3",
	},
	fieldRow: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(1.5, 0),
		borderBottom: "1px solid #f0f0f0",
		"&:last-child": {
			borderBottom: "none",
		},
	},
	fieldLabel: {
		fontWeight: 500,
		color: "#555",
		minWidth: 140,
		fontSize: "0.9rem",
	},
	fieldValue: {
		color: "#333",
		fontFamily: "monospace",
		backgroundColor: "#f8f9fa",
		padding: theme.spacing(0.5, 1),
		borderRadius: 4,
		fontSize: "0.85rem",
		border: "1px solid #e9ecef",
	},
	chip: {
		fontSize: "0.75rem",
		height: 24,
	},
	adminChip: {
		backgroundColor: "#d32f2f",
		color: "white",
	},
	userChip: {
		backgroundColor: "#1976d2",
		color: "white",
	},
	yesChip: {
		backgroundColor: "#388e3c",
		color: "white",
	},
	noChip: {
		backgroundColor: "#757575",
		color: "white",
	},
	userAgent: {
		fontSize: "0.8rem",
		color: "#666",
		backgroundColor: "#f8f9fa",
		padding: theme.spacing(1.5),
		border: "1px solid #e9ecef",
		borderRadius: 4,
		fontFamily: "monospace",
		lineHeight: 1.4,
		wordBreak: "break-all",
	},
}));

const SimpleField = ({ label, value, isChip = false, chipType = "default" }) => {
	const classes = useStyles();

	const renderValue = () => {
		if (isChip) {
			let chipClass = classes.chip;
			if (chipType === "admin") chipClass += ` ${classes.adminChip}`;
			else if (chipType === "user") chipClass += ` ${classes.userChip}`;
			else if (chipType === "yes") chipClass += ` ${classes.yesChip}`;
			else if (chipType === "no") chipClass += ` ${classes.noChip}`;

			return <Chip label={value} className={chipClass} size="small" />;
		}
		return <span className={classes.fieldValue}>{value}</span>;
	};

	return (
		<div className={classes.fieldRow}>
			<Typography className={classes.fieldLabel}>{label}:</Typography>
			<Box ml={2}>{renderValue()}</Box>
		</div>
	);
};

const LoginDetailsContent = () => {
	const classes = useStyles();
	const record = useRecordContext();

	if (!record) return null;

	const getRoleChipType = (role) => {
		return role === "admin" ? "admin" : "user";
	};

	const getCodeChipType = (loggedViaCode) => {
		return loggedViaCode ? "yes" : "no";
	};

	return (
		<div className={classes.container}>
			<Card className={classes.mainCard}>
				<div className={classes.header}>
					<IconButton className={classes.backButton} onClick={() => window.history.back()}>
						<ArrowBackIcon />
					</IconButton>
					<Typography className={classes.title}>Login ID #{record.id}</Typography>
				</div>

				<div className={classes.content}>
					<Grid container spacing={3}>
						<Grid item xs={12} md={6}>
							<div className={classes.section}>
								<Typography className={classes.sectionTitle}>
									<PersonIcon className={classes.sectionIcon} />
									User Information
								</Typography>
								<SimpleField label="User ID" value={record.user_id} />
								<div className={classes.fieldRow}>
									<Typography className={classes.fieldLabel}>User Name:</Typography>
									<Box ml={2}>
										<ReferenceField source="user_id" reference="users" link={false}>
											<FunctionField
												render={(record) => (
													<span className={classes.fieldValue}>
														{record ? `${record.first_name} ${record.last_name}` : "Loading..."}
													</span>
												)}
											/>
										</ReferenceField>
									</Box>
								</div>
								<SimpleField label="Email" value={record.email} />
								<SimpleField
									label="Role"
									value={record.user_role}
									isChip
									chipType={getRoleChipType(record.user_role)}
								/>
								<SimpleField
									label="Auth Method"
									value={record.logged_via_code ? "Code" : "Password"}
									isChip
									chipType={getCodeChipType(record.logged_via_code)}
								/>
							</div>
						</Grid>

						<Grid item xs={12} md={6}>
							<div className={classes.section}>
								<Typography className={classes.sectionTitle}>
									<ComputerIcon className={classes.sectionIcon} />
									Session Information
								</Typography>
								<SimpleField label="IP Address" value={record.ip_address} />
								<SimpleField label="Location" value={record.location} />
								<SimpleField label="Login Time" value={formatDateTimeString(record.created_at)} />
							</div>
						</Grid>

						<Grid item xs={12}>
							<div className={classes.section}>
								<Typography className={classes.sectionTitle}>
									<SecurityIcon className={classes.sectionIcon} />
									Device Information
								</Typography>
								<Typography className={classes.fieldLabel} style={{ marginBottom: 8 }}>
									User Agent:
								</Typography>
								<div className={classes.userAgent}>{record.user_agent}</div>
							</div>
						</Grid>
					</Grid>
				</div>
			</Card>
		</div>
	);
};

export const LoginLogsShow = (props) => {
	return (
		<Show {...props}>
			<LoginDetailsContent />
		</Show>
	);
};
